class SearchPopup {
  constructor() {
    this.popup = document.getElementById('search-popup');
    this.searchInput = this.popup?.querySelector('[data-search-input]');
    this.searchResults = this.popup?.querySelector('[data-search-results]');
    this.searchContent = this.popup?.querySelector('[data-search-content]');
    this.searchLoading = this.popup?.querySelector('[data-search-loading]');
    this.searchNoResults = this.popup?.querySelector('[data-search-no-results]');
    this.closeButton = this.popup?.querySelector('.search-popup__close');
    this.overlay = this.popup?.querySelector('.search-popup__overlay');
    this.searchButtons = document.querySelectorAll('.header__search');

    this.searchTimeout = null;
    this.currentQuery = '';
    this.abortController = null;

    this.init();
  }

  init() {
    if (!this.popup) return;

    // Open popup on search button click
    this.searchButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    // Close popup
    this.closeButton?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());

    // Close on click outside content
    this.popup.addEventListener('click', (e) => {
      if (e.target === this.popup || e.target.classList.contains('search-popup__container')) {
        this.close();
      }
    });

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.popup.classList.contains('active')) {
        this.close();
      }
    });

    // Search input
    this.searchInput?.addEventListener('input', (e) => {
      this.handleSearchInput(e.target.value);
    });

    // Prevent form submission if not needed
    this.popup.querySelector('.search-popup__form')?.addEventListener('submit', (e) => {
      if (this.currentQuery.trim() === '') {
        e.preventDefault();
      }
    });
  }

  open() {
    this.popup.classList.add('active');
    document.body.classList.add('lock');

    // Focus on input
    setTimeout(() => {
      this.searchInput?.focus();
    }, 100);
  }

  close() {
    this.popup.classList.remove('active');
    document.body.classList.remove('lock');

    // Clear search
    if (this.searchInput) {
      this.searchInput.value = '';
    }
    this.clearResults();
  }

  handleSearchInput(query) {
    // Clear previous timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    // Abort previous request
    if (this.abortController) {
      this.abortController.abort();
    }

    this.currentQuery = query.trim();

    // Clear results if query is empty
    if (this.currentQuery === '') {
      this.clearResults();
      return;
    }

    // Show loading state
    this.showLoading();

    // Debounce search
    this.searchTimeout = setTimeout(() => {
      this.performSearch(this.currentQuery);
    }, 300);
  }

  async performSearch(query) {
    try {
      this.abortController = new AbortController();

      // Use Shopify's suggest.json endpoint with SKU and variant searching
      const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=12&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.title,variants.sku,vendor,tag`;

      const response = await fetch(url, {
        signal: this.abortController.signal
      });

      if (!response.ok) {
        throw new Error('Search request failed');
      }

      const data = await response.json();

      // Extract products from JSON response
      this.renderResults(data, query);

    } catch (error) {
      if (error.name === 'AbortError') {
        // Request was aborted, ignore
        return;
      }
      console.error('Search error:', error);
      this.hideLoading();
    }
  }

  renderResults(data, query) {
    this.hideLoading();

    // Extract products from suggest.json response
    const products = this.extractProducts(data);

    if (products.length === 0) {
      this.showNoResults();
      return;
    }

    this.hideNoResults();
    this.searchContent.innerHTML = products.map(product => this.renderProduct(product)).join('');
  }

  extractProducts(data) {
    const products = [];

    // Extract products from suggest.json response
    if (data.resources && data.resources.results && data.resources.results.products) {
      data.resources.results.products.forEach(product => {
        // Get the first available image
        let image = null;
        if (product.image) {
          image = product.image;
        } else if (product.images && product.images.length > 0) {
          image = product.images[0];
        }

        // Get price - Shopify API returns price in cents
        let price = null;
        let comparePrice = null;

        // Try to get price from product object directly
        if (product.price) {
          price = product.price;
        } else if (product.variants && product.variants.length > 0) {
          const variant = product.variants[0];
          price = variant.price;
          comparePrice = variant.compare_at_price;
        }

        // Fallback to product.price_min if available
        if (!price && product.price_min) {
          price = product.price_min;
        }

        console.log('Product:', product.title, 'Price:', price); // Debug

        products.push({
          url: product.url,
          title: product.title,
          image: image,
          imageAlt: product.title,
          price: price,
          comparePrice: comparePrice,
          vendor: product.vendor,
          productType: product.product_type
        });
      });
    }

    return products;
  }

  renderProduct(product) {
    const imageHtml = product.image
      ? `<img class="search-popup__product-image" src="${product.image}" alt="${product.imageAlt}" loading="lazy">`
      : `<img class="search-popup__product-image" src="/assets/placeholder.png" alt="Placeholder">`;

    // Format price
    let priceHtml = '';
    if (product.price) {
      const formattedPrice = this.formatPrice(product.price);
      const formattedComparePrice = product.comparePrice ? this.formatPrice(product.comparePrice) : null;

      if (formattedComparePrice && product.comparePrice > product.price) {
        priceHtml = `
          <div class="search-popup__product-price">
            <span class="search-popup__product-price--compare">${formattedComparePrice}</span>
            ${formattedPrice}
          </div>
        `;
      } else {
        priceHtml = `<div class="search-popup__product-price">${formattedPrice}</div>`;
      }
    }

    return `
      <a href="${product.url}" class="search-popup__product">
        <div class="search-popup__product-image-wrapper">
          ${imageHtml}
        </div>
        <div class="search-popup__product-info">
          <div class="search-popup__product-title">
            <h3>${product.title}</h3>
          </div>
          ${priceHtml}
        </div>
      </a>
    `;
  }

  formatPrice(price) {
    // Shopify API returns price as a number (already in dollars)
    const amount = parseFloat(price).toFixed(2);
    return `$${amount}`;
  }

  showLoading() {
    this.searchLoading.style.display = 'flex';
    this.searchContent.style.display = 'none';
    this.searchNoResults.style.display = 'none';
  }

  hideLoading() {
    this.searchLoading.style.display = 'none';
    this.searchContent.style.display = 'grid';
  }

  showNoResults() {
    this.searchContent.style.display = 'none';
    this.searchNoResults.style.display = 'block';
  }

  hideNoResults() {
    this.searchNoResults.style.display = 'none';
  }

  clearResults() {
    this.searchContent.innerHTML = '';
    this.hideLoading();
    this.hideNoResults();
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new SearchPopup();
  });
} else {
  new SearchPopup();
}
